import React, { useCallback } from 'react'
import { useMedia, useMemo, useEffect, useRef } from 'sb-hooks'
import { openModal, modalVisibility, ModalVisibilityProps, Modal } from 'sb-modal'
import { useConnect } from 'sb-redux-hook'
import { useLocation } from 'react-router-dom'
import { constants } from 'common/helpers'

import { Box } from 'components/_new/layout'
import { ContentLoader } from 'components/_new/ui'
import { buttonMessages } from 'components/_new/controls'

import Carousel from 'components/ui/Carousel/Carousel'
import Product from 'components/product/Product/Product'

import s from './EmptyQueueRecommendationsModal.scss'

import useData from './util/useData'


const EmptyQueueRecommendationsModal: React.FunctionComponent<ModalVisibilityProps> = (props) => {
  const { title, metadata, snowplowMetadata, products, isFetching } = useData()

  const { isMobile } = useMedia()

  const { onClose } = props

  // close on path change
  const { pathname } = useLocation()
  const previousPathnameRef = useRef(null)

  useEffect(() => {
    if (previousPathnameRef.current && previousPathnameRef.current !== pathname) {
      onClose()
    }
    else {
      previousPathnameRef.current = pathname
    }
  }, [ pathname, onClose ])

  const { isNotSubscribed } = useConnect({
    isNotSubscribed: 'models.subscription.isNotSubscribed',
  })

  const handleAfterAdd = useCallback(() => {
    if (isNotSubscribed) {
      onClose()
    }
  }, [ isNotSubscribed, onClose ])

  const items = useMemo(() => {
    return products.map((product) => (
      <Product
        key={product.id}
        containerClassName={s.product}
        data={product}
        addTo="queue"
        isQuickViewVisible
        metadata={metadata}
        snowplowMetadata={snowplowMetadata}
        onQuickViewClick={onClose}
        onAfterAdd={handleAfterAdd}
      />
    ))
  }, [ products, metadata, snowplowMetadata, onClose, handleAfterAdd ])

  const carouselOptions = useMemo(() => ({
    dots: true,
    arrows: true,
    slidesToShow: 5,
    slidesToScroll: 5,
    responsive: [ {
      breakpoint: constants.media.desktopStart,
      settings: {
        slidesToShow: 4,
        slidesToScroll: 4,
      },
    }, {
      breakpoint: constants.media.tabletLandscapeStart,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 2,
      },
    } ],
  }), [])

  const node = (
    isMobile ? (
      <div className={s.products}>
        {items}
      </div>
    ) : (
      <Carousel options={carouselOptions}>
        {items}
      </Carousel>
    )
  )

  const buttonProps = useMemo(() => {
    if (!isMobile) {
      return null
    }

    return {
      title: buttonMessages.back,
      onClick: onClose,
    }
  }, [ onClose, isMobile ])

  return (
    <Modal
      className={s.modal}
      title={title}
      ordinaryButton={buttonProps}
      onClose={onClose}
    >
      <Box mt={isMobile ? null : 16}>
        {
          isFetching ? (
            <ContentLoader />
          ) : (
            node
          )
        }
      </Box>
    </Modal>
  )
}


export const openEmptyQueueRecommendationsModal = () => {
  openModal('emptyQueueRecommendations')
}


export default modalVisibility('emptyQueueRecommendations', EmptyQueueRecommendationsModal)
